import React, { useState, useRef } from 'react'
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    Switch,
    FormControlLabel,
    Grid,
    Card,
    CardContent,
    LinearProgress,
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Dayjs } from 'dayjs'
import 'dayjs/locale/tr'
import {
    CloudDownload,
    CloudUpload,
    Schedule,
    RestaurantMenu,
    Refresh,
    Send
} from '@mui/icons-material'
import * as XLSX from 'xlsx'
import { ConfirmationModal } from '../ConfirmationModal'
import { useSendMealAllowanceMutation } from '../../pages/cards/services/services'
import { useSelector } from 'react-redux'
import { RootState } from '../../store/store'
import { encrypt } from '../../utils/encrypt'
import { decrypt } from '../../utils/decrypt'
import { enqueueSnackbar } from 'notistack'
import { useSendMealAllowanceListMutation } from '../../pages/approvals/services/approvalService'

interface MealAllowanceData {
    description: string
    receiverCardNo: string
    currency: string
    value: number | string
    __rowNum__: number
}

interface ValidationResult {
    class: string
    text: string
}

interface LoadMealBalanceByCardNoProps {
    onClose?: () => void
}

const LoadMealBalanceByCardNo = ({ onClose }: LoadMealBalanceByCardNoProps = {}) => {
    const [loading, setLoading] = useState(false)
    const [formData, setFormData] = useState<MealAllowanceData[]>([])
    const [invalidRows, setInvalidRows] = useState<MealAllowanceData[]>([])
    const [validRows, setValidRows] = useState<MealAllowanceData[]>([])
    const [isShowSendMoneyButton, setIsShowSendMoneyButton] = useState(false)
    const [isScheduled, setIsScheduled] = useState(false)
    const [scheduledDate, setScheduledDate] = useState<Dayjs | null>(null)
    const [message, setMessage] = useState('')
    const [messageType, setMessageType] = useState<'success' | 'error' | 'warning' | 'info'>('info')
    const [showNoValidRowsWarning, setShowNoValidRowsWarning] = useState(false)
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [isConfirmLoading, setIsConfirmLoading] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    // Redux ve API hooks
    const { currentUserState } = useSelector((state: RootState) => state.currentUserState)
    const [sendMealAllowanceList, { isLoading: apiLoading }] = useSendMealAllowanceListMutation()

    const fileFormat = ['.xlsx', '.csv', '.xls', '.xlsm', '.xlsb', '.xlt']

    const downloadSampleFile = () => {
        const excelFilePath = `${process.env.PUBLIC_URL}/assets/data/SendMealAllowanceByCardNo.xlsx`
        const a = document.createElement('a')
        a.href = excelFilePath
        a.download = 'KartNoIleYemekKartiTopluBakiye.xlsx'
        a.click()
    }

    const getCurrency = (data: string): string => {
        switch (data) {
            case 'USD': return '2'
            case 'EUR': return '3'
            case 'TL': return '1'
            default: return '1'
        }
    }

    const formatCurrency = (value: string, currency: string): string => {
        if (value != null && currency != null) {
            const currencyCode = getProperCurrencyCode(currency)
            const formatter = new Intl.NumberFormat('tr-TR', {
                style: 'currency',
                currency: currencyCode,
                minimumFractionDigits: 2,
            })
            return formatter.format(parseFloat(value))
        }
        return ''
    }

    const getProperCurrencyCode = (currency: string): string => {
        switch (currency) {
            case '1':
            case 'TL':
                return 'TRY'
            case '2':
            case 'USD':
                return 'USD'
            case '3':
            case 'EUR':
                return 'EUR'
            default:
                return 'TRY'
        }
    }

    const parseNumericValue = (value: any): number => {
        if (value === null || value === undefined) {
            return NaN
        }

        if (typeof value === 'number') {
            return value
        }

        if (typeof value === 'string') {
            const trimmed = value.trim()

            if (trimmed === '') {
                return NaN
            }

            const cleaned = trimmed.replace(/\s/g, '')
            const commaCount = (cleaned.match(/,/g) || []).length
            const dotCount = (cleaned.match(/\./g) || []).length
            let normalized = cleaned

            if (commaCount > 0 && dotCount > 0) {
                if (cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')) {
                    normalized = cleaned.replace(/\./g, '').replace(',', '.')
                } else {
                    normalized = cleaned.replace(/,/g, '')
                }
            } else if (commaCount > 0) {
                normalized = cleaned.replace(',', '.')
            } else if (dotCount > 1) {
                normalized = cleaned.replace(/\./g, '')
            }

            const parsed = Number(normalized)

            if (!Number.isNaN(parsed)) {
                return parsed
            }

            const fallback = Number(trimmed.replace(',', '.'))

            return Number.isNaN(fallback) ? NaN : fallback
        }

        return NaN
    }

    const getCellClassAndText = (value: any, type: string): ValidationResult => {
        if (type === 'receiverCardNo' || type === 'currency') {
            return value
                ? { class: 'text-green-800 font-bold', text: value }
                : { class: 'text-red-800 font-bold', text: 'Eksik' }
        }

        if (type === 'value') {
            if (typeof value === 'string') {
                return { class: 'text-red-800 font-bold', text: 'Hatalı' }
            }
            return value
                ? { class: 'text-green-800 font-bold', text: value }
                : { class: 'text-red-800 font-bold', text: 'Eksik' }
        }

        return { class: '', text: '' }
    }

    const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || event.target.files.length === 0) {
            setLoading(false)
            return
        }

        setLoading(true)
        const selectedFile = event.target.files[0]

        if (fileFormat.some((format) => selectedFile.name.endsWith(format))) {
            const fileReader = new FileReader()
            fileReader.onload = async (e: any) => {
                try {
                    const binaryData = e.target.result
                    const workbook = XLSX.read(binaryData, { type: 'binary' })
                    const sheetName = workbook.SheetNames[0]
                    const worksheet = workbook.Sheets[sheetName]

                    // Excel başlıklarını kontrol et - İlk satır uyarı metni, ikinci satır başlıklar
                    const allRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null }) as any[][]

                    // İkinci satırı başlık olarak kullan (index 1)
                    const headerRow = allRows[1] as any[]

                    // Başlıkları temizle ve normalize et
                    const cleanHeaders = headerRow.map((h, idx) => {
                        if (h && typeof h === 'string') {
                            return h.trim()
                        }
                        // Eğer başlık yoksa, sütun harfi kullan (A, B, C, D...)
                        return String.fromCharCode(65 + idx) // A=65, B=66, etc.
                    })

                    // Üçüncü satırdan itibaren verileri oku (index 2 ve sonrası)
                    const dataRows = allRows.slice(2)

                    // Verileri başlıklarla eşleştir
                    const excelData: any[] = dataRows.map((row, rowIndex) => {
                        const rowObj: any = {}
                        cleanHeaders.forEach((header, colIndex) => {
                            rowObj[header] = row[colIndex] !== undefined ? row[colIndex] : null
                        })
                        return rowObj
                    })

                    const allKeys = excelData.length > 0 ? Object.keys(excelData[0]) : []

                    // Sütun adlarını normalize et (boşlukları normalize et, küçük harfe çevir)
                    const normalizeForMatch = (name: string): string => {
                        return name
                            .toLowerCase()
                            .trim()
                            .replace(/\s+/g, ' ') // Çoklu boşlukları tek boşluğa çevir
                    }

                    // Excel sütun adlarını dinamik olarak bul - daha esnek eşleştirme
                    const findColumn = (targetNames: string[], rowKeys: string[]): string | null => {
                        // 1. Tam eşleşme (büyük/küçük harf duyarsız, boşluk normalize)
                        for (const target of targetNames) {
                            const normalizedTarget = normalizeForMatch(target)
                            for (const key of rowKeys) {
                                if (normalizeForMatch(key) === normalizedTarget) {
                                    return key
                                }
                            }
                        }

                        // 2. Kısmi eşleşme - normalize edilmiş string'lerin birbirini içerip içermediğini kontrol et
                        for (const target of targetNames) {
                            const normalizedTarget = normalizeForMatch(target)
                            for (const key of rowKeys) {
                                const normalizedKey = normalizeForMatch(key)
                                // Her iki yönde de kontrol et
                                if (normalizedKey.includes(normalizedTarget) || normalizedTarget.includes(normalizedKey)) {
                                    // Minimum 3 karakter eşleşmesi olsun
                                    if (normalizedTarget.length >= 3 && normalizedKey.length >= 3) {
                                        return key
                                    }
                                }
                            }
                        }

                        return null
                    }

                    // Excel sütun adlarını farklı varyasyonlarla kontrol et
                    const mappedData = excelData.map<MealAllowanceData | null>((row, index) => {
                        const rowKeys = Object.keys(row)

                        // Her satır için sütun eşleştirmesi yap
                        const descriptionCol = findColumn(['Açıklama'], rowKeys)
                        const cardNoCol = findColumn(['Alıcı Kart No', 'AlıcıKartNo', 'Kart No'], rowKeys)
                        const currencyCol = findColumn(['Para Birimi', 'ParaBirimi'], rowKeys)
                        const valueCol = findColumn(['Tutar'], rowKeys)

                        // Debug: İlk satırda eşleşmeleri log'la
                        if (index === 0) {
                        }

                        // Değerleri al
                        const getValue = (colName: string | null) => {
                            if (!colName || row[colName] === undefined || row[colName] === null) {
                                return null
                            }
                            const val = row[colName]
                            if (typeof val === 'string') {
                                return val.trim()
                            }
                            return val
                        }

                        const descriptionVal = getValue(descriptionCol)
                        const cardNoVal = getValue(cardNoCol)
                        const currencyVal = getValue(currencyCol)
                        const valueVal = getValue(valueCol)

                        const mappedRow = {
                            description: descriptionVal || '',
                            receiverCardNo: cardNoVal || '',
                            currency: currencyVal || '',
                            value: valueVal !== null ? valueVal : '',
                            __rowNum__: index + 3 // Excel'de satır numarası (1: uyarı, 2: başlık, 3+: veri)
                        }

                        const isCompletelyEmpty =
                            mappedRow.description === '' &&
                            mappedRow.receiverCardNo === '' &&
                            mappedRow.currency === '' &&
                            (mappedRow.value === '' || mappedRow.value === null)

                        return isCompletelyEmpty ? null : mappedRow
                    })
                        .filter((row): row is MealAllowanceData => row !== null)

                    // Boş satırları filtrele (tüm alanlar boş olan satırlar)
                    const filteredData = mappedData.filter(row => {
                        const hasDescription = row.description && String(row.description).trim().length > 0
                        const hasCardNo = row.receiverCardNo && String(row.receiverCardNo).trim().length > 0
                        const hasCurrency = row.currency && String(row.currency).trim().length > 0
                        const hasValue = row.value !== null && row.value !== undefined && String(row.value).trim() !== ''

                        // En az bir alan dolu ise satırı dahil et
                        return hasDescription || hasCardNo || hasCurrency || hasValue
                    })
                    const validRowsTemp: MealAllowanceData[] = []
                    const invalidRowsTemp: MealAllowanceData[] = []

                    // Eğer tüm satırlar boşsa, Excel boş olarak kabul et
                    if (filteredData.length === 0) {
                        setMessage('Excel dosyası boş veya geçersiz format.')
                        setMessageType('error')
                        setValidRows([])
                        setInvalidRows([])
                        setFormData([])
                        setIsShowSendMoneyButton(false)
                        setShowNoValidRowsWarning(false)
                        setLoading(false)
                        return
                    }

                    for (let i = 0; i < filteredData.length; i++) {
                        const row = filteredData[i]
                        const cardNo = row.receiverCardNo ? String(row.receiverCardNo).trim() : ''
                        const currencyValue = row.currency ? String(row.currency).trim().toUpperCase() : ''
                        const descriptionValue = row.description ? String(row.description).trim() : ''
                        const parsedValue = parseNumericValue(row.value)

                        // Daha detaylı ve tutarlı validasyon
                        let isRowValid = true

                        // Kart numarası validasyonu
                        if (!cardNo) {
                            isRowValid = false
                        }

                        // Para birimi validasyonu
                        if (!currencyValue || !['TL', 'USD', 'EUR'].includes(currencyValue)) {
                            isRowValid = false
                        }

                        // Tutar validasyonu
                        if (Number.isNaN(parsedValue) || parsedValue <= 0) {
                            isRowValid = false
                        }

                        if (isRowValid) {
                            validRowsTemp.push({
                                ...row,
                                description: descriptionValue,
                                receiverCardNo: cardNo,
                                currency: currencyValue,
                                value: parsedValue
                            })
                        } else {
                            invalidRowsTemp.push({
                                ...row,
                                description: descriptionValue,
                                receiverCardNo: cardNo,
                                currency: currencyValue || row.currency,
                                value: row.value
                            })
                        }
                    }

                    // Valid satırları sırala (row number'a göre)
                    validRowsTemp.sort((a, b) => a.__rowNum__ - b.__rowNum__)
                    invalidRowsTemp.sort((a, b) => a.__rowNum__ - b.__rowNum__)
                    // Mesaj gösterme mantığı
                    if (validRowsTemp.length > 0 && invalidRowsTemp.length > 0) {
                        setMessage(`Excel dosyası yüklendi. Doğru satırlar: ${validRowsTemp.length}, Hatalı satırlar: ${invalidRowsTemp.length}`)
                        setMessageType('info')
                    } else if (validRowsTemp.length > 0 && invalidRowsTemp.length === 0) {
                        setMessage(`Excel dosyası başarıyla yüklendi. Toplam ${validRowsTemp.length} satır işlenecek.`)
                        setMessageType('success')
                    } else if (validRowsTemp.length === 0 && invalidRowsTemp.length > 0) {
                        setMessage(`Excel dosyası yüklendi ancak hiçbir geçerli satır bulunamadı.`)
                        setMessageType('warning')
                    } else {
                        setMessage('')
                    }

                    setValidRows(validRowsTemp)
                    setInvalidRows(invalidRowsTemp)
                    setFormData(filteredData)
                    setIsShowSendMoneyButton(validRowsTemp.length > 0)
                    setShowNoValidRowsWarning(validRowsTemp.length === 0 && invalidRowsTemp.length > 0)
                    setLoading(false)
                } catch (error) {
                    setMessage('Excel dosyası okunurken hata oluştu. Lütfen dosya formatını kontrol edin.')
                    setMessageType('error')
                    setLoading(false)
                }
            }
            fileReader.readAsBinaryString(selectedFile)
        } else {
            setLoading(false)
            setMessage(`Dosya formatı ${fileFormat.join(', ')} olmalıdır.`)
            setMessageType('error')
        }
    }

    const resetUpload = () => {
        setFormData([])
        setValidRows([])
        setInvalidRows([])
        setIsShowSendMoneyButton(false)
        setLoading(false)
        setIsScheduled(false)
        setScheduledDate(null)
        setMessage('')
        setShowNoValidRowsWarning(false)
        setShowConfirmModal(false)
        setIsConfirmLoading(false)
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const sendMoneyRequest = () => {
        if (validRows.length > 0) {
            setShowConfirmModal(true)
        } else {
            setMessage('Gönderilecek doğru satır bulunamadı.')
            setMessageType('error')
        }
    }

    const handleConfirmSend = async () => {
        if (!currentUserState?.customerId || !currentUserState?.companyId) {
            enqueueSnackbar('Kullanıcı bilgileri bulunamadı', { variant: 'error' })
            return
        }

        if (validRows.length === 0) {
            enqueueSnackbar('Gönderilecek geçerli satır bulunamadı', { variant: 'error' })
            return
        }

        setIsConfirmLoading(true)

        try {
            // Excel'den gelen veriyi API formatına çevir
            const mealAllowanceData = validRows.map(row => {
                const parsedValue = parseNumericValue(row.value)

                return {
                    description: row.description || '',
                    receiverCardNo: encrypt(row.receiverCardNo), // Kart numarasını şifrele
                    currencyId: getCurrency(row.currency), // Para birimi ID'sine çevir
                    value: Number.isNaN(parsedValue) ? 0 : parsedValue
                }
            })

            const payload = {
                data: mealAllowanceData,
                companyId: currentUserState.companyId,
                customerId: currentUserState.customerId,
                scheduledAt: isScheduled && scheduledDate ? scheduledDate.format('YYYY-MM-DDTHH:mm:ss') : null
            }

            const result = await sendMealAllowanceList(payload)

            const responseData = result.data ?? (result as any).error?.data

            if (result.data?.success) {
                enqueueSnackbar(result.data.message || 'Yemek yardımı başarıyla gönderildi', {
                    variant: 'success'
                })
                setShowConfirmModal(false)
                resetUpload()
                onClose?.()
            } else {
                // Middleware'de zaten kart numaraları decode edilip mesaja ekleniyor
                // Burada sadece mesajı gösteriyoruz
                const errorMessage = responseData?.message || 'İşlem başarısız'
                setMessage(errorMessage)
                setMessageType('error')
            }
        } catch (error: any) {

            // Middleware'de zaten kart numaraları decode edilip mesaja ekleniyor
            // Burada sadece mesajı gösteriyoruz
            const errorMessage = error?.data?.message || error?.message || 'Yemek yardımı gönderilirken hata oluştu'
            setMessage(errorMessage)
            setMessageType('error')
        } finally {
            setIsConfirmLoading(false)
        }
    }

    const handleCancelSend = () => {
        setShowConfirmModal(false)
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
            <Box sx={{ p: 3 }}>
                {/* Header */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.3)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <RestaurantMenu sx={{ mr: 2, color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a202c' }}>
                            Kart No ile Bakiye Yükleme
                        </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#64748b' }}>
                        Excel dosyası yükleyerek toplu olarak yemek kartlarına bakiye yükleyebilirsiniz
                    </Typography>
                </Paper>

                {loading && <LinearProgress sx={{ mb: 2 }} />}

                {/* File Upload Section */}
                {!isShowSendMoneyButton && (
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {/* Download Sample Excel */}
                        <Grid item xs={12} md={4}>
                            <Card
                                sx={{
                                    height: 200,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    border: '2px dashed #e0e7ff',
                                    '&:hover': {
                                        borderColor: '#3b82f6',
                                        bgcolor: 'rgba(59, 130, 246, 0.05)'
                                    }
                                }}
                                onClick={downloadSampleFile}
                            >
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <CloudDownload sx={{ fontSize: 60, color: '#64748b', mb: 2 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                        Örnek Excel Dosyasını İndiriniz
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Upload Excel */}
                        <Grid item xs={12} md={8}>
                            <Card
                                sx={{
                                    height: 200,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    border: '2px dashed #e0e7ff',
                                    '&:hover': {
                                        borderColor: '#3b82f6',
                                        bgcolor: 'rgba(59, 130, 246, 0.05)'
                                    }
                                }}
                                onClick={() => {
                                    // Dosya input'unu temizle ki aynı dosya tekrar seçilebilsin
                                    if (fileInputRef.current) {
                                        fileInputRef.current.value = ''
                                    }
                                    fileInputRef.current?.click()
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <CloudUpload sx={{ fontSize: 60, color: '#64748b', mb: 2 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                        Excel Yükle
                                    </Typography>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={onFileChange}
                                        onClick={(e) => {
                                            // Input'a tıklandığında değeri temizle
                                            e.currentTarget.value = ''
                                        }}
                                        style={{ display: 'none' }}
                                        accept={fileFormat.join(',')}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Message Display */}
                {message && (
                    <Alert severity={messageType} sx={{ mb: 3 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }} >
                            {message}
                        </Typography>
                    </Alert>
                )}

                {/* Reload Button - Excel yüklendikten sonra tekrar yüklemeye izin ver */}
                {(formData.length > 0 || validRows.length > 0 || invalidRows.length > 0) && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<Refresh />}
                            onClick={resetUpload}
                            sx={{ fontWeight: 600, fontSize: '.9rem' }}
                        >
                            Yeni Excel Yükle
                        </Button>
                    </Box>
                )}

                {/* Invalid Rows Table */}
                {isShowSendMoneyButton && invalidRows.length > 0 && (
                    <Paper sx={{ mb: 3 }}>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Hatalı Satırlar
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Hatalı Satır İndeksi</TableCell>
                                        <TableCell>Kart Numarası</TableCell>
                                        <TableCell>Para Birimi</TableCell>
                                        <TableCell>Tutar</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invalidRows.map((row, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>{row.__rowNum__}</TableCell>
                                            <TableCell sx={{ color: getCellClassAndText(row.receiverCardNo, 'receiverCardNo').class === 'text-green-800 font-bold' ? 'green' : 'red' }}>
                                                {getCellClassAndText(row.receiverCardNo, 'receiverCardNo').text}
                                            </TableCell>
                                            <TableCell sx={{ color: getCellClassAndText(row.currency, 'currency').class === 'text-green-800 font-bold' ? 'green' : 'red' }}>
                                                {getCellClassAndText(row.currency, 'currency').text}
                                            </TableCell>
                                            <TableCell sx={{ color: getCellClassAndText(row.value, 'value').class === 'text-green-800 font-bold' ? 'green' : 'red' }}>
                                                {getCellClassAndText(row.value, 'value').text}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                {/* Warning Message - Hatalı satır varsa göster */}
                {invalidRows.length > 0 && isShowSendMoneyButton && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        <Typography sx={{ fontWeight: 600 }}>
                            ❌ Hatalı Kayıtlar Tespit Edildi!
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Excel dosyanızda <strong>{invalidRows.length}</strong> hatalı satır bulundu.
                            Hatalı kayıtlar düzeltilmeden işlem başlatılamaz.
                            Lütfen hataları düzeltin ve dosyayı yeniden yükleyin.
                        </Typography>
                    </Alert>
                )}

                {/* Valid Rows Table - Geçerli satırlar varsa her zaman göster */}
                {(isShowSendMoneyButton || validRows.length > 0) && validRows.length > 0 && (
                    <Paper sx={{ mb: 3 }}>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                İşlemler ({validRows.length} satır)
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Satır İndeksi</TableCell>
                                        <TableCell>Kart Numarası</TableCell>
                                        <TableCell>Tutar</TableCell>
                                        <TableCell>Para Birimi</TableCell>
                                        <TableCell>Açıklama</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {validRows.map((data, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>{data.__rowNum__}</TableCell>
                                            <TableCell>{data.receiverCardNo}</TableCell>
                                            <TableCell>{formatCurrency(data.value.toString(), getCurrency(data.currency))}</TableCell>
                                            <TableCell>{data.currency}</TableCell>
                                            <TableCell>{data.description || '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                {/* Scheduler Section - Geçerli satırlar varsa göster */}
                {formData.length > 0 && isShowSendMoneyButton && validRows.length > 0 && invalidRows.length === 0 && (
                    <Paper sx={{ mb: 3 }}>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                                <Schedule sx={{ mr: 1 }} />
                                Zamanlayıcı Ayarları
                            </Typography>
                        </Box>
                        <CardContent>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isScheduled}
                                        onChange={(e) => setIsScheduled(e.target.checked)}
                                    />
                                }
                                label="Ödemeyi İleri Bir Tarihe Planla"
                                sx={{ mb: 2 }}
                            />

                            {isScheduled && (
                                <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 2 }}>
                                    <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                                        Planlanan Ödeme Tarihi
                                    </Typography>
                                    <DateTimePicker
                                        label="Tarih ve Saat Seçiniz"
                                        value={scheduledDate}
                                        onChange={(newValue) => setScheduledDate(newValue)}
                                        format="DD/MM/YYYY HH:mm"
                                        slotProps={{
                                            textField: {
                                                size: 'medium',
                                                fullWidth: true,
                                                sx: { maxWidth: 300 }
                                            },
                                            day: {
                                                sx: {
                                                    fontSize: '.9rem',
                                                    width: 40,
                                                    height: 40,
                                                },
                                            },
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                background: 'white',
                                                '&:hover': {
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'primary.main',
                                                    },
                                                },
                                            }
                                        }}
                                    />
                                </Box>
                            )}
                        </CardContent>
                    </Paper>
                )}

                {/* Error Message - Sadece hatalı satırlar varsa ve geçerli satır yoksa göster */}
                {showNoValidRowsWarning && validRows.length === 0 && invalidRows.length > 0 && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        <Typography sx={{ fontWeight: 600 }}>
                            ❌ Kritik Hata! Hiçbir satır işlenemiyor.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Toplam <strong>{invalidRows.length}</strong> hatalı satır tespit edildi.
                            Lütfen Excel dosyanızdaki verileri kontrol edin ve yeniden yükleyin.
                        </Typography>
                    </Alert>
                )}

                {/* Send Money Button - Geçerli satırlar varsa ve hatalı kayıt yoksa göster */}
                {isShowSendMoneyButton && validRows.length > 0 && invalidRows.length === 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<Send />}
                            onClick={sendMoneyRequest}
                            sx={{
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                }
                            }}
                        >
                            Para Gönderme İşlemini Başlat ({validRows.length} satır)
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={showConfirmModal}
                onClose={handleCancelSend}
                onConfirm={handleConfirmSend}
                title="Bakiye Yükleme Onayı"
                content={`${validRows.length} adet karta bakiye yüklemek istediğinizden emin misiniz?`}
                confirmButtonText="Onayla ve Başlat"
                closeButtonText="İptal"
                isLoading={isConfirmLoading || apiLoading}
                optionalWidth="auto"
            >
                {isScheduled && scheduledDate && (
                    <Alert severity="info" sx={{ mt: 2, mx: 2 }}>
                        <Typography variant="body2">
                            <strong>Planlanan Tarih:</strong> {scheduledDate.format('DD/MM/YYYY HH:mm')}
                        </Typography>
                    </Alert>
                )}
            </ConfirmationModal>
        </LocalizationProvider>
    )
}

export default LoadMealBalanceByCardNo

